import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FileText } from 'lucide-react';
import { useAgreements, useAgreement, useAgreementTemplates } from '@/composables/financial/useAgreements';
import { useContacts } from '@/composables/crm/useContacts';
import { useInvoices } from '@/composables/financial/useInvoices';
import { useSignatures } from '@/composables/shared/useSignatures';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormDatePicker } from '@/components/forms/FormDatePicker';
import { VariableInput } from '@/components/agreement/VariableInput';
import { AgreementLivePreview } from '@/components/agreement/AgreementLivePreview';
import { formatFullName } from '@/lib/formatters';
import { CURRENCIES } from '@/lib/constants';
import type { AgreementTemplate, TemplateVariable } from '@/types/table';

interface AgreementFormShape {
  templateId: string;
  title: string;
  agreementDate: string;
  contactId: string;
  signatureId: string;
  invoiceId: string;
  currency: string;
  totalAmount: number;
  notes: string;
}

export function AgreementCreatePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;

  const { templates } = useAgreementTemplates();
  const { contacts } = useContacts();
  const { invoices } = useInvoices();
  const { signatures, defaultSignature } = useSignatures();
  const { settings: orgSettings } = useOrganizationSettings();
  const { createAgreement, updateAgreement } = useAgreements();
  const { agreement: existing, isLoading: loadingExisting } = useAgreement(id || '');

  const [templateId, setTemplateId] = useState<string>('');
  const [variableValues, setVariableValues] = useState<Record<string, unknown>>({});
  const [hiddenVariables, setHiddenVariables] = useState<string[]>([]);

  const form = useForm<AgreementFormShape>({
    defaultValues: {
      templateId: '',
      title: '',
      agreementDate: new Date().toISOString().slice(0, 10),
      contactId: '',
      signatureId: '',
      invoiceId: '',
      currency: 'USD',
      totalAmount: 0,
      notes: '',
    },
  });

  // Pre-select default signature on create
  useEffect(() => {
    if (isEdit || !defaultSignature) return;
    if (!form.getValues('signatureId')) form.setValue('signatureId', defaultSignature.id);
  }, [isEdit, defaultSignature, form]);

  // Hydrate from existing agreement when editing
  useEffect(() => {
    if (!isEdit || !existing) return;
    setTemplateId(existing.templateId || '');
    setVariableValues((existing.variableValues as Record<string, unknown>) || {});
    setHiddenVariables(existing.hiddenVariables || []);
    form.reset({
      templateId: existing.templateId || '',
      title: existing.title,
      agreementDate: (existing.agreementDate || '').slice(0, 10),
      contactId: existing.contactId || '',
      signatureId: existing.signatureId || '',
      invoiceId: existing.invoiceId || '',
      currency: existing.currency || 'USD',
      totalAmount: existing.totalAmount ? Number(existing.totalAmount) : 0,
      notes: existing.notes || '',
    });
  }, [isEdit, existing, form]);

  const selectedTemplate = useMemo<AgreementTemplate | undefined>(
    () => templates.find((t) => t.id === (templateId || form.watch('templateId'))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templates, templateId, form.watch('templateId')],
  );

  // When template changes (on create), seed defaults ONLY for manual-source
  // variables. Sourced variables (org / contact / signature / orgPolicy /
  // agreement) are left empty so they auto-fill from the live values instead
  // of being pinned to a stale template default (e.g. COMPANY_IDENTIFIER_LABEL
  // would otherwise stick at "GSTIN" instead of the org's actual label).
  useEffect(() => {
    if (isEdit) return;
    if (!selectedTemplate) return;
    const next: Record<string, unknown> = {};
    for (const v of selectedTemplate.variables) {
      if (v.defaultValue !== undefined && (!v.source || v.source === 'manual')) {
        next[v.key] = v.defaultValue;
      }
    }
    setVariableValues(next);
    setHiddenVariables([]);
    form.setValue('templateId', selectedTemplate.id);
  }, [isEdit, selectedTemplate, form]);

  // Auto-fill from selected contact (visible in form sidebar)
  const selectedContactId = form.watch('contactId');
  const selectedContact = contacts.find((c) => c.id === selectedContactId);

  // Live preview values: merge user-typed variables with computed scalars + auto-fills (lightweight)
  const previewValues = useMemo(() => {
    const fromOrg = orgSettings ? {
      COMPANY_LEGAL_NAME:           orgSettings.companyName,
      COMPANY_EMAIL:                orgSettings.companyEmail,
      COMPANY_IDENTIFIER:           orgSettings.taxId,
      COMPANY_IDENTIFIER_LABEL:     orgSettings.companyIdentifierLabel,
      COMPANY_ADDRESS:              [orgSettings.addressLine1, orgSettings.addressLine2, [orgSettings.city, orgSettings.state, orgSettings.postalCode].filter(Boolean).join(', '), orgSettings.country].filter(Boolean).join('\n'),
      GOVERNING_COUNTRY:            orgSettings.governingCountry,
      JURISDICTION_LOCATION:        orgSettings.jurisdictionLocation,
      LATE_PAYMENT_PERCENTAGE:      orgSettings.lateFeePercent,
      CLIENT_INACTIVITY_DAYS:       orgSettings.clientInactivityDays,
      FREE_SUPPORT_DURATION:        orgSettings.freeSupportDays,
      DELIVERABLE_REVIEW_DAYS:      orgSettings.deliverableReviewDays,
    } : {};
    const sigId = form.watch('signatureId') || defaultSignature?.id;
    const sig = sigId ? signatures.find((s) => s.id === sigId) : undefined;
    const fromSignature = sig ? {
      AUTHORIZED_PERSON_NAME:        sig.signatoryName,
      AUTHORIZED_PERSON_DESIGNATION: sig.signatoryTitle,
    } : {};
    const assembleAddr = (a?: { address?: string; city?: string; state?: string; country?: string; postalCode?: string }) =>
      a ? [a.address, [a.city, a.state, a.postalCode].filter(Boolean).join(', '), a.country].filter(Boolean).filter((x) => x && x.trim()).join('\n') : '';
    const companyAddr = assembleAddr(selectedContact?.company);
    const contactAddr = assembleAddr(selectedContact);
    const fromContact = selectedContact ? {
      CLIENT_REPRESENTATIVE_NAME:        `${selectedContact.firstName} ${selectedContact.lastName}`,
      CLIENT_REPRESENTATIVE_DESIGNATION: selectedContact.jobTitle,
      CLIENT_EMAIL:                      selectedContact.email,
      CLIENT_LEGAL_NAME:                 selectedContact.company?.name,
      // Prefer the company's address; fall back to the contact's own address.
      CLIENT_ADDRESS:                    companyAddr || contactAddr,
    } : {};
    const fromInvoice = (() => {
      const invId = form.watch('invoiceId');
      const inv = invId ? invoices.find((i) => i.id === invId) : undefined;
      return inv ? { INVOICE_NUMBER: inv.invoiceNumber } : {};
    })();
    const fromAgreement = {
      AGREEMENT_DATE:        form.watch('agreementDate'),
      TOTAL_CONTRACT_VALUE:  form.watch('totalAmount') ? { amount: Number(form.watch('totalAmount')), currency: form.watch('currency') } : undefined,
    };
    return { ...fromOrg, ...fromSignature, ...fromContact, ...fromInvoice, ...fromAgreement, ...variableValues };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSettings, signatures, defaultSignature, selectedContact, invoices, variableValues, form.watch('signatureId'), form.watch('invoiceId'), form.watch('agreementDate'), form.watch('totalAmount'), form.watch('currency')]);

  const onSubmit = (data: AgreementFormShape) => {
    if (!selectedTemplate) return;
    const payload = {
      templateId: selectedTemplate.id,
      title: data.title,
      agreementDate: data.agreementDate,
      contactId: data.contactId || undefined,
      signatureId: data.signatureId || undefined,
      invoiceId: data.invoiceId || undefined,
      currency: data.currency,
      totalAmount: Number(data.totalAmount) || undefined,
      notes: data.notes || undefined,
      variableValues,
      hiddenVariables,
    };
    if (isEdit && id) {
      updateAgreement.mutate({ id, data: payload }, { onSuccess: () => navigate(`/financial/agreements/${id}`) });
    } else {
      createAgreement.mutate(payload, { onSuccess: (res) => {
        const newId = res?.data?.id;
        navigate(newId ? `/financial/agreements/${newId}` : '/financial/agreements');
      } });
    }
  };

  if (isEdit && loadingExisting) {
    return <PageContainer backTo title="Edit Agreement"><div className="text-muted-foreground text-sm">Loading…</div></PageContainer>;
  }

  // Step 1: template picker (only on create, until a template is chosen)
  if (!isEdit && !selectedTemplate) {
    return (
      <PageContainer backTo title="New Agreement">
        <div className="max-w-3xl">
          <h3 className="text-base font-medium mb-1">Pick a template</h3>
          <p className="text-sm text-muted-foreground mb-4">You can customize variables in the next step.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                onClick={() => setTemplateId(tpl.id)}
                className="text-left rounded-md border bg-card p-4 hover:border-primary hover:shadow-md transition-all space-y-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">{tpl.name}</span>
                  </div>
                  <div className="flex gap-1">
                    <Badge variant="outline" className="text-[10px]">{tpl.kind}</Badge>
                    {tpl.isBuiltIn && <Badge variant="secondary" className="text-[10px]">Built-in</Badge>}
                  </div>
                </div>
                {tpl.description && <p className="text-xs text-muted-foreground line-clamp-2">{tpl.description}</p>}
                <div className="text-xs text-muted-foreground">{tpl.variables?.length ?? 0} variables</div>
              </button>
            ))}
          </div>
        </div>
      </PageContainer>
    );
  }

  // Step 2: fill form + live preview
  return (
    <PageContainer backTo
      title={isEdit ? 'Edit Agreement' : 'New Agreement'}
      
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,580px)]">
          <div className="space-y-6 min-w-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Basics</CardTitle>
                <CardDescription>Template: <span className="font-medium text-foreground">{selectedTemplate?.name}</span></CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField form={form} name="title" label="Title" required placeholder="e.g. Acme website rebuild — Phase 1" />
                <div className="grid grid-cols-2 gap-4">
                  <FormDatePicker form={form} name="agreementDate" label="Agreement Date" required />
                  <FormSelect form={form} name="signatureId" label="Signature" options={signatures.map((s) => ({ label: `${s.label}${s.isDefault ? ' (Default)' : ''}`, value: s.id }))} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Client</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <FormSelect form={form} name="contactId" label="Contact" options={contacts.map(c => ({ label: formatFullName(c.firstName, c.lastName) + (c.company?.name ? ` — ${c.company.name}` : ''), value: c.id }))} />
                {selectedContact?.company && (
                  <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2">
                    Client info auto-fills from <span className="font-medium text-foreground">{selectedContact.company.name}</span> (override in variables below).
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Contract Value</CardTitle>
                <CardDescription>The total agreement value; individual milestones reference percentages of this.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormSelect form={form} name="currency" label="Currency" options={CURRENCIES.map((c) => ({ label: c, value: c }))} />
                  <FormField form={form} name="totalAmount" label="Total Contract Value" type="decimal" />
                </div>
                <div className="space-y-1.5">
                  <Label>Referenced Invoice (optional)</Label>
                  <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={form.watch('invoiceId') || ''} onChange={(e) => form.setValue('invoiceId', e.target.value, { shouldDirty: true })}>
                    <option value="">— None —</option>
                    {invoices.map((inv) => (
                      <option key={inv.id} value={inv.id}>{inv.invoiceNumber} — {inv.billingName || inv.billingCompanyName || 'no client'}</option>
                    ))}
                  </select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Variables</CardTitle>
                <CardDescription>Fields prefilled from contact / org / signature show a tag; you can override anything.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {(selectedTemplate?.variables || []).map((v: TemplateVariable) => (
                  <VariableInput
                    key={v.key}
                    variable={v}
                    value={variableValues[v.key] ?? previewValues[v.key as keyof typeof previewValues]}
                    onChange={(next) => setVariableValues({ ...variableValues, [v.key]: next })}
                    hidden={hiddenVariables.includes(v.key)}
                    onToggleHidden={() => setHiddenVariables((prev) => prev.includes(v.key) ? prev.filter((k) => k !== v.key) : [...prev, v.key])}
                  />
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Notes (internal)</CardTitle></CardHeader>
              <CardContent>
                <FormTextarea form={form} name="notes" label="Notes" />
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
              <Button type="submit" disabled={createAgreement.isPending || updateAgreement.isPending}>
                {(createAgreement.isPending || updateAgreement.isPending) ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Agreement')}
              </Button>
            </div>
          </div>

          {/* Sticky live preview */}
          <div className="min-w-0">
            <div className="sticky top-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Live Preview</CardTitle>
                  <CardDescription>How the document will look</CardDescription>
                </CardHeader>
                <CardContent>
                  <AgreementLivePreview
                    body={selectedTemplate?.body || ''}
                    variables={selectedTemplate?.variables || []}
                    values={previewValues}
                    hiddenVariables={hiddenVariables}
                    primaryColor={orgSettings?.primaryColor}
                    accentColor={orgSettings?.accentColor}
                    title={form.watch('title')}
                    agreementDate={form.watch('agreementDate')}
                    kind={selectedTemplate?.kind}
                    agreementNumber={isEdit ? existing?.agreementNumber : undefined}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </form>
    </PageContainer>
  );
}
